import React, { useState } from "react";
import { Box, Tab, Tabs, Typography, useTheme } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { FormattedMessage, useIntl } from "react-intl";
import {
  cmmsTypeScale,
  getAssetHierarchyHeaderSx,
  getAssetHierarchyPalette,
  getPageHeaderIconSx,
  getPageShellSx,
  getPageSubtitleSx,
  getPageTitleSx,
  getSectionBoxSx,
  getTabsSx,
} from "../cmms/assetHierarchyStyles";
import messages from "./messages";
import StorageLocationsPanel from "./components/StorageLocationsPanel";
import SparePartTypesPanel from "./components/SparePartTypesPanel";
import TradesPanel from "./components/TradesPanel";
import NotificationPreferencesPanel from "./components/NotificationPreferencesPanel";

/*
 * The CMMS's own settings page: storage locations, spare part types, trades and email
 * notification preferences.
 *
 * A page rather than a section in features/settings, which is OEE's: a flat company form with no
 * tabs, and the wrong place for a module that has been kept separate everywhere else.
 *
 * The tabs are a list rather than a conditional because settings arrive one at a time — trades
 * was the fourth, and it was an entry in that array rather than a reason to take this file apart.
 * Same shape as AssetNodeDetailsPanel's tabs.
 *
 * The route is Admin-only (AppRoutes.js). Everything reachable from here is Cmms_Manager, so for
 * any other role this would be a settings page with nothing settable.
 */
export default function CmmsSettings() {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  const [tab, setTab] = useState(0);

  const tabs = [
    {
      key: "storageLocations",
      label: intl.formatMessage(messages.tabStorageLocations),
      render: () => <StorageLocationsPanel />,
    },
    // The two controlled vocabularies together, notifications last: the first two are lists the
    // spare part form picks from, the third is about who gets email.
    {
      key: "sparePartTypes",
      label: intl.formatMessage(messages.tabSparePartTypes),
      render: () => <SparePartTypesPanel />,
    },
    // After the vocabularies and before notifications: a register like them, but of people
    // rather than of things on a shelf.
    {
      key: "trades",
      label: intl.formatMessage(messages.tabTrades),
      render: () => <TradesPanel />,
    },
    {
      key: "notifications",
      label: intl.formatMessage(messages.tabNotifications),
      render: () => <NotificationPreferencesPanel />,
    },
  ];

  // Clamped rather than trusted. The index is state, and a tab list that shrinks — as it will the
  // first time one of these is put behind a flag — would otherwise leave it pointing past the end.
  const activeTabIndex = Math.min(tab, tabs.length - 1);
  const activeTab = tabs[activeTabIndex];

  return (
    <Box sx={getPageShellSx()}>
      <Box sx={getAssetHierarchyHeaderSx(cmmsColors)}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <SettingsOutlinedIcon sx={getPageHeaderIconSx(cmmsColors)} />
          <Box>
            <Typography variant={cmmsTypeScale.pageTitle} sx={getPageTitleSx(cmmsColors)}>
              <FormattedMessage {...messages.pageTitle} />
            </Typography>
            <Typography variant={cmmsTypeScale.pageSubtitle} sx={getPageSubtitleSx(cmmsColors)}>
              <FormattedMessage {...messages.pageSubtitle} />
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Outside the header, as on the machine page: the strip sits on the page, on top of what it switches. */}
      <Tabs
        value={activeTabIndex}
        onChange={(_event, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ ...getTabsSx(cmmsColors), mt: 2 }}
      >
        {tabs.map((t) => (
          <Tab disableRipple key={t.key} label={t.label} />
        ))}
      </Tabs>

      <Box sx={getSectionBoxSx(cmmsColors)}>{activeTab?.render()}</Box>
    </Box>
  );
}
